import { FlowExtensionError } from './base'
import { FlowExtensionErrorCodes } from './codes'

// Concrete flow-extension errors. Messages are kept stable (callers/tests rely on them); the `code` is the
// machine-resolvable identity.

export class UnknownMethodError extends FlowExtensionError {
  constructor(serviceId: string, methodName: string) {
    super(
      FlowExtensionErrorCodes.UNKNOWN_METHOD,
      404,
      `[flow-extension:${ serviceId }] unknown method "${ methodName }"`,
      { serviceId, methodName }
    )
  }
}

export class MissingMethodParamError extends FlowExtensionError {
  constructor(serviceId: string, param: string) {
    super(
      FlowExtensionErrorCodes.MISSING_METHOD_PARAM,
      400,
      `[flow-extension:${ serviceId }] missing required "${ param }" in method input`,
      { serviceId, param }
    )
  }
}

// A single schema violation, flattened out of zod so the details survive the worker IPC boundary.
export interface ValidationIssue {
  // Dot/bracket path inside the validated value ('' when the value itself is invalid).
  path: string
  message: string
  code?: string
}

function formatIssues(issues: ValidationIssue[]): string {
  return issues.map(issue => (issue.path ? `${ issue.path }: ${ issue.message }` : issue.message)).join('; ')
}

// A method was called with input that doesn't match its schema — action params, a helper `payload`, or a
// SYSTEM method's invocation. `target` names the validated slot so the caller can point at it.
export class InvalidMethodInputError extends FlowExtensionError {
  constructor(serviceId: string, methodName: string, target: string, issues: ValidationIssue[]) {
    super(
      FlowExtensionErrorCodes.INVALID_METHOD_INPUT,
      400,
      `[flow-extension:${ serviceId }] invalid ${ target } for method "${ methodName }" — ${ formatIssues(issues) }`,
      { serviceId, methodName, target, issues }
    )
  }
}

// The `criteria` of a dictionary/loader call failed its declared schema. Separate from the payload error so
// the console can surface "these dependent fields are wrong" rather than "the whole call is malformed".
export class InvalidCriteriaError extends FlowExtensionError {
  constructor(serviceId: string, methodName: string, issues: ValidationIssue[]) {
    super(
      FlowExtensionErrorCodes.INVALID_CRITERIA,
      400,
      `[flow-extension:${ serviceId }] invalid criteria for method "${ methodName }" — ${ formatIssues(issues) }`,
      { serviceId, methodName, target: 'criteria', issues }
    )
  }
}

export class DuplicateItemIdError extends FlowExtensionError {
  constructor(serviceId: string, id: string, kind: string) {
    super(
      FlowExtensionErrorCodes.DUPLICATE_ITEM_ID,
      400,
      `[flow-extension:${ serviceId }] duplicate id "${ id }" (${ kind }) — ids must be unique across all registered items`,
      { serviceId, id, kind }
    )
  }
}

export class ExtensionNotRegisteredError extends FlowExtensionError {
  constructor(serviceId: string, entryPoint?: string) {
    super(
      FlowExtensionErrorCodes.EXTENSION_NOT_REGISTERED,
      404,
      `[flow-extension] no v2 extension registered for serviceId="${ serviceId }"${ entryPoint ? ` (${ entryPoint })` : '' }`,
      { serviceId, entryPoint }
    )
  }
}

export class InvalidConfigError extends FlowExtensionError {
  // Raised when the resolved `configItems` do not satisfy what the service declared. Carries one issue per
  // offending field, so the console can point at the config form rather than reporting the whole call.
  constructor(serviceId: string, issues: ValidationIssue[]) {
    super(
      FlowExtensionErrorCodes.INVALID_CONFIG,
      400,
      `[flow-extension:${ serviceId }] invalid config — ${ formatIssues(issues) }`,
      { serviceId, issues }
    )
  }
}

export class InvalidSchemaError extends FlowExtensionError {
  // `message` is composed by the guard, which knows how to describe what was received and what to do
  // about it (see v2/schema/assert-object-schema.ts).
  constructor(message: string, details: Record<string, unknown>) {
    super(FlowExtensionErrorCodes.INVALID_SCHEMA, 400, message, details)
  }
}

export class InvalidTriggerConfigError extends FlowExtensionError {
  // `message` carries the specifics (kept verbatim for existing tests).
  constructor(serviceId: string, message: string, details?: Record<string, unknown>) {
    super(FlowExtensionErrorCodes.INVALID_TRIGGER_CONFIG, 400, `[flow-extension:${ serviceId }] ${ message }`, { serviceId, ...details })
  }
}

export class UnknownTriggerError extends FlowExtensionError {
  constructor(serviceId: string, triggerId: string, kind = 'polling') {
    super(
      FlowExtensionErrorCodes.UNKNOWN_TRIGGER,
      404,
      `[flow-extension:${ serviceId }] no ${ kind } trigger "${ triggerId }"`,
      { serviceId, triggerId, kind }
    )
  }
}

export class RealtimeSetupError extends FlowExtensionError {
  constructor(serviceId: string, message: string) {
    super(FlowExtensionErrorCodes.REALTIME_SETUP, 400, `[flow-extension:${ serviceId }] ${ message }`, { serviceId })
  }
}

export class OAuthSetupError extends FlowExtensionError {
  constructor(serviceId: string, message: string) {
    super(FlowExtensionErrorCodes.OAUTH_SETUP, 400, `[flow-extension:${ serviceId }] ${ message }`, { serviceId })
  }
}

export class FeatureNotEnabledError extends FlowExtensionError {
  constructor(feature: string, hint: string) {
    super(
      FlowExtensionErrorCodes.FEATURE_NOT_ENABLED,
      400,
      `[flow-extension] "${ feature }" is not enabled for this extension — ${ hint }`,
      { feature }
    )
  }
}

export class InvalidDictionaryOutputScheme extends FlowExtensionError {
  constructor(serviceId: string, dictionaryName: string) {
    super(
      FlowExtensionErrorCodes.INVALID_DICTIONARY_OUTPUT_SCHEME,
      400,
      `[flow-extension:${ serviceId }] dictionary "${ dictionaryName }" execution returns wrong result, it must result list of object {value, label, hist} or object {cursor:'string', items:[...]}`,
      { serviceId, dictionaryName }
    )
  }
}
